import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import { useRouter } from "@tanstack/react-router";
import clsx from "clsx";
import dayjs from "dayjs";
import { Fragment, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { Divider } from "@/components/Divider/Divider";
import { AirlineAvatar } from "@/features/flights/components/AirlineAvatar";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { TripType, type FlightType } from "@/features/flights/types/types";
import type { FindFlightOffersOfferItemV3 } from "@/types/codegen";
import { parseDuration } from "@/utils/duration";

import { FlightOriginAndDestination } from "../FlightOriginAndDestination";

import { SegmentDetails } from "./SegmentDetails";
import { StopsLabel } from "./StopsLabel";
import { TimelineDot } from "./TimelineDot";
import { TimelineLine } from "./TimelineLine";
import { TransferBar } from "./TransferBar";
import {
  getNumberOfCarriersHandlingFlight,
  isStopOvernight,
  mapCabinClasses,
} from "./utils";

type CollapsibleRowProps = {
  isExpanded: boolean;
  children: React.ReactNode;
  subgrid?: boolean;
};

function CollapsibleRow({
  isExpanded,
  children,
  subgrid = false,
}: CollapsibleRowProps) {
  return (
    <div
      className={clsx(
        "col-span-3 grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out",
        subgrid && "grid-cols-subgrid",
        isExpanded ? "[grid-template-rows:1fr]" : "[grid-template-rows:0fr]",
      )}
      aria-hidden={!isExpanded}
    >
      <div
        className={clsx(
          "min-h-0",
          subgrid && "col-span-3 grid grid-cols-subgrid",
        )}
      >
        {children}
      </div>
    </div>
  );
}

type Params = {
  flight: FlightType;
  offerItems?: FindFlightOffersOfferItemV3[];
  isDepartureFlight?: boolean;
  isInteractive?: boolean;
};

export function FlightDetailsCard({
  flight,
  offerItems,
  isDepartureFlight,
  isInteractive = true,
}: Params) {
  const { history } = useRouter();
  const intl = useIntl();

  const { searchData } = useFlightSearchDataContext();

  const segments = flight?.segments || [];
  const stops = flight?.stops || [];
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];
  const numberOfCarriers = getNumberOfCarriersHandlingFlight(flight);
  const firstSegmentDatesDayDifference = dayjs(firstSegment.arrival.date).diff(
    dayjs(firstSegment.departure.date),
    "day",
  );
  const lastSegmentDatesDayDifference = dayjs(lastSegment.arrival.date).diff(
    dayjs(lastSegment.departure.date),
    "day",
  );

  const [isExpanded, setIsExpanded] = useState(isInteractive ? false : true);

  return (
    <div className="section-card pb-2">
      <div className="mb-4 flex items-center gap-2">
        <div className="relative">
          <AirlineAvatar
            airline={firstSegment.marketingCarrier.airline}
            size="large"
          />
          {numberOfCarriers > 1 ? (
            <div className="absolute right-[-4px] bottom-[-4px] flex size-5 items-center justify-center rounded-full border-2 border-white bg-gray-200 text-[10px] text-black">
              {numberOfCarriers - 1}+
            </div>
          ) : null}
        </div>
        <div className="flex flex-col justify-between">
          <FlightOriginAndDestination flight={flight} className="font-medium" />
          <div className="text-sm text-text-tertiary">
            {intl.formatMessage(
              {
                defaultMessage: "{hours}h {minutes}m",
                description:
                  "Formatted flight or travel duration (e.g. '2h 30m')",
              },
              parseDuration(flight.duration),
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-[max-content_min-content_auto]">
        <div className="pb-4">
          <h3 className="text-end font-medium text-text-title">
            {firstSegment.departure?.time}
          </h3>
          <div className="text-end text-sm text-text-tertiary">
            {firstSegment.departure?.date
              ? dayjs(firstSegment.departure.date).format("ddd, DD MMM")
              : null}
          </div>
        </div>

        <div className="px-4 pt-3">
          <TimelineLine>
            <TimelineDot />
          </TimelineLine>
        </div>

        <div className="pb-4">
          <div className="flex items-center gap-1 font-medium">
            <h3 className="text-text-title">
              {firstSegment.departure?.city.name}
            </h3>
            <div className="size-[3px] rounded-full bg-text-primary" />
            <h3 className="text-text-title">
              {firstSegment.departure?.airport.code}
            </h3>
          </div>
          <div className="text-sm text-text-tertiary">
            {firstSegment.departure?.airport.name}
          </div>
        </div>

        {/* EXPANDABLE FLIGHT STOPS INFO */}
        <CollapsibleRow isExpanded={!isExpanded} subgrid>
          <div className="pb-4" />
          <div className="h-full px-4">
            <TimelineLine />
          </div>
          <div className="pb-4">
            <StopsLabel
              stops={flight.stops}
              firstStopCity={firstSegment.arrival.city.name}
            />
          </div>
        </CollapsibleRow>

        {/* START OF EXPANDABLE ROW */}
        {flight.stops && flight.stops.length > 0 ? (
          flight.segments.map((segment, segmentIndex) => {
            const cabinClasses = mapCabinClasses(segment, offerItems);
            const segmentDepartureAndArrivalDayDifference = dayjs(
              segment.arrival.date,
            ).diff(dayjs(segment.departure.date), "day");

            return (
              <Fragment key={segment.key}>
                {/* EVERY SEGMENT EXCEPT FIRST */}
                {segmentIndex === 0 ? null : (
                  <>
                    <CollapsibleRow isExpanded={isExpanded} subgrid>
                      <div className="pb-4">
                        <h3 className="text-end font-medium text-text-title">
                          {segment.departure?.time}
                        </h3>
                        <div className="text-end text-sm text-text-tertiary">
                          {segment.departure?.date
                            ? dayjs(segment.departure.date).format(
                                "ddd, DD MMM",
                              )
                            : null}
                        </div>
                      </div>
                      <div className="h-full px-4 pt-3">
                        <TimelineLine>
                          <TimelineDot />
                        </TimelineLine>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 font-medium">
                          <h3 className="text-text-title">
                            {segment.departure.city.name}
                          </h3>
                          <div className="size-[3px] rounded-full bg-text-primary" />
                          <h3 className="text-text-title">
                            {segment.departure.airport.code}
                          </h3>
                        </div>
                        <div className="text-sm text-text-tertiary">
                          {segment.departure.airport.name}
                        </div>
                      </div>
                    </CollapsibleRow>

                    {segmentIndex === flight.segments.length - 1 ? (
                      <CollapsibleRow isExpanded={isExpanded} subgrid>
                        <div />
                        <div className="h-full px-4">
                          <TimelineLine />
                        </div>
                        <div className="pb-4">
                          <SegmentDetails
                            segment={segment}
                            cabinClasses={cabinClasses}
                            isOvernight={
                              segmentDepartureAndArrivalDayDifference > 0
                            }
                          />
                        </div>
                      </CollapsibleRow>
                    ) : null}
                  </>
                )}
                {/* EVERY SEGMENT EXCEPT LAST */}
                {segmentIndex === flight.segments.length - 1 ? null : (
                  <>
                    <CollapsibleRow isExpanded={isExpanded} subgrid>
                      <div className="pb-4" />
                      <div className="h-full px-4">
                        <TimelineLine />
                      </div>
                      <div className="pb-4">
                        <SegmentDetails
                          segment={segment}
                          cabinClasses={cabinClasses}
                          isOvernight={
                            segmentDepartureAndArrivalDayDifference > 0
                          }
                        />
                      </div>
                    </CollapsibleRow>

                    <CollapsibleRow isExpanded={isExpanded} subgrid>
                      <div className="pb-4">
                        <h3 className="text-end font-medium text-text-title">
                          {segment.arrival?.time}
                        </h3>
                        <div
                          className={clsx("text-end text-sm", {
                            "text-semantic-error-strong":
                              segmentDepartureAndArrivalDayDifference > 0,
                            "text-text-tertiary":
                              segmentDepartureAndArrivalDayDifference === 0,
                          })}
                        >
                          {segment.arrival?.date ? (
                            <span>
                              {dayjs(segment.arrival.date).format(
                                "ddd, DD MMM",
                              )}
                              {segmentDepartureAndArrivalDayDifference > 0 ? (
                                <sup>
                                  +{segmentDepartureAndArrivalDayDifference}
                                </sup>
                              ) : null}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="h-3 px-4">
                        <TimelineLine>
                          <TimelineDot align="bottom" />
                        </TimelineLine>
                      </div>
                      <div className="pb-4">
                        <div className="flex items-center gap-1 font-medium">
                          <h3 className="text-text-title">
                            {segment.arrival.city.name}
                          </h3>
                          <div className="size-[3px] rounded-full bg-text-primary" />
                          <h3 className="text-text-title">
                            {segment.arrival.airport.code}
                          </h3>
                        </div>
                        <div className="text-sm text-text-tertiary">
                          {segment.arrival.airport.name}
                        </div>
                      </div>
                    </CollapsibleRow>

                    {stops[segmentIndex]?.stopTime && (
                      <CollapsibleRow isExpanded={isExpanded}>
                        <div className="pb-4">
                          <TransferBar
                            stopTime={stops[segmentIndex].stopTime}
                            cityName={segment.arrival.city.name}
                            isOvernight={isStopOvernight(
                              dayjs(
                                `${segment.arrival.date}, ${segment.arrival.time}`,
                              ),
                              parseDuration(stops[segmentIndex]?.stopTime),
                            )}
                          />
                        </div>
                      </CollapsibleRow>
                    )}
                  </>
                )}
              </Fragment>
            );
          })
        ) : (
          <CollapsibleRow isExpanded={isExpanded} subgrid>
            <div className="pb-4" />
            <div className="h-full px-4">
              <TimelineLine />
            </div>
            <div className="pb-4">
              <SegmentDetails
                segment={firstSegment}
                cabinClasses={mapCabinClasses(firstSegment, offerItems)}
                isOvernight={firstSegmentDatesDayDifference > 0}
              />
            </div>
          </CollapsibleRow>
        )}
        {/* END OF EXPANDABLE ROW */}

        <div className="pb-4">
          <h3 className="text-end font-medium text-text-title">
            {lastSegment.arrival?.time}
          </h3>
          <div
            className={clsx("text-end text-sm", {
              "text-semantic-error-strong": lastSegmentDatesDayDifference > 0,
              "text-text-tertiary": lastSegmentDatesDayDifference === 0,
            })}
          >
            {lastSegment.arrival?.date ? (
              <span>
                {dayjs(lastSegment.arrival.date).format("ddd, DD MMM")}
                {dayjs(lastSegment.arrival.date).diff(
                  dayjs(lastSegment.departure.date),
                  "day",
                ) > 0 ? (
                  <sup>
                    +
                    {dayjs(lastSegment.arrival.date).diff(
                      dayjs(firstSegment.departure.date),
                      "day",
                    )}
                  </sup>
                ) : null}
              </span>
            ) : null}
          </div>
        </div>

        <div className="h-3 px-4">
          <TimelineLine>
            <TimelineDot align="bottom" />
          </TimelineLine>
        </div>

        <div className="pb-4">
          <div className="flex items-center gap-1 font-medium">
            <h3 className="text-text-title">
              {lastSegment.arrival?.city.name}
            </h3>
            <div className="size-[3px] rounded-full bg-text-primary" />
            <h3 className="text-text-title">
              {lastSegment.arrival?.airport.code}
            </h3>
          </div>
          <div className="text-sm text-text-tertiary">
            {lastSegment.arrival?.airport.name}
          </div>
        </div>
      </div>
      {isInteractive ? (
        <>
          <Divider color="neutral-soft" />
          <div className="mt-2 flex justify-between py-2.5">
            <Button
              type="button"
              variant="textFit"
              size="small"
              onClick={() =>
                history.go(
                  searchData?.tripType === TripType.ROUND_TRIP &&
                    isDepartureFlight
                    ? -2
                    : -1,
                )
              }
            >
              {intl.formatMessage({
                defaultMessage: "Change flight",
                description:
                  "Button to swap the currently selected flight for a different one",
              })}
            </Button>

            <Button
              type="button"
              variant="textFit"
              size="small"
              startIcon={
                isExpanded ? (
                  <CaretUpIcon size={16} />
                ) : (
                  <CaretDownIcon size={16} />
                )
              }
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded
                ? intl.formatMessage({
                    defaultMessage: "Hide details",
                    description:
                      "Button or link to collapse and hide expanded details",
                  })
                : intl.formatMessage({
                    defaultMessage: "Show details",
                    description: "Button or link to reveal hidden details",
                  })}
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
